import React from 'react';
import { i18n } from 'config/locales';
import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import Textarea from 'components/Form/Textarea';
import DateRange from 'components/Form/DateRange';
import Text from 'components/Form/Text';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import { SubstanceDrugsClearanceItemModel } from 'forms/sections/substance/drugs/clearance/types';
import {
  CommonOnUpdate, CommonScrollIntoView, CommonOnError, CommonRequired
} from 'types/props';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import Comments from 'components/Form/Comments';
import { isPVQForm } from 'helpers/form';
import DrugType from './DrugType';

interface Props extends Partial<SubstanceDrugsClearanceItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  scrollIntoView?: CommonScrollIntoView
  onError?: CommonOnError
  required?: CommonRequired
  formType?: string
}

export default class DrugClearanceUse extends ValidationElement<Props> {
  update = (updateValues: Partial<SubstanceDrugsClearanceItemModel>) => {
    if (this.props.onUpdate) {
      this.props.onUpdate({
        Description: this.props.Description,
        InvolvementDates: this.props.InvolvementDates,
        EstimatedUse: this.props.EstimatedUse,
        Comments: this.props.Comments,
        DrugType: this.props.DrugType, // PVQ Only
        DrugTypeOther: this.props.DrugTypeOther, // PVQOnly
        HowManyTimes: this.props.HowManyTimes, // PVQOnly
        Reason: this.props.Reason, // PVQOnly
        ...updateValues,
      });
    }
  };

  updateDrugType = (values: SubstanceDrugsClearanceItemModel['DrugType']) => {
    this.update({ DrugType: values });
  };

  updateDrugTypeOther = (values: SubstanceDrugsClearanceItemModel['DrugTypeOther']) => {
    this.update({ DrugTypeOther: values });
  };

  updateHowManyTimes = (values: SubstanceDrugsClearanceItemModel['HowManyTimes']) => {
    this.update({ HowManyTimes: values });
  };

  updateReason = (values: SubstanceDrugsClearanceItemModel['Reason']) => {
    this.update({ Reason: values });
  };

  updateDescription = (values: SubstanceDrugsClearanceItemModel['Description']) => {
    this.update({ Description: values });
  };

  updateInvolvementDates = (values: SubstanceDrugsClearanceItemModel['InvolvementDates']) => {
    this.update({ InvolvementDates: values });
  };

  updateEstimatedUse = (values: SubstanceDrugsClearanceItemModel['EstimatedUse']) => {
    this.update({ EstimatedUse: values });
  };

  updateComments = (values: SubstanceDrugsClearanceItemModel['Comments']) => {
    this.update({ Comments: values });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;

    const isPvq = isPVQForm(this.props.formType);
    return isPvq ? (
      <div className="drug-clearance-use">
        <Field
          title={i18n.t('substance.pvq.drugs.clearance.heading.drugType')}
          className="drug-type-use"
          attribute={`${attribute}.DrugType`}
          adjustFor="labels"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DrugType
            {...this.props.DrugType}
            attribute={attribute}
            name="DrugType"
            onUpdate={this.updateDrugType}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            formType={this.props.formType}
          />
        </Field>

        {this.props.DrugType?.value === 'Other' && (

          <Field
            title={i18n.t('substance.pvq.drugs.clearance.heading.otherDrugType')}
            attribute={`${attribute}.DrugTypeOther`}
            scrollIntoView={this.props.scrollIntoView}
          >
            <Textarea
              {...this.props.DrugTypeOther}
              name="DrugTypeOther"
              attribute={`${attribute}.DrugTypeOther`}
              className="drugTypeOther"
              onUpdate={this.updateDrugTypeOther}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        )}

        <Field
          attribute={`${attribute}.InvolvementDates`}
          adjustFor="daterange"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateRange
            {...this.props.InvolvementDates}
            attribute={`${attribute}.InvolvementDates`}
            className="involvement-dates"
            fromDateLabel={i18n.t('substance.pvq.drugs.clearance.heading.involvementDatesFrom')}
            hideFromDay
            hideToDay
            minDateEqualTo
            name="InvolvementDates"
            onError={this.props.onError}
            onUpdate={this.updateInvolvementDates}
            required={this.props.required}
            toDateLabel={i18n.t('substance.pvq.drugs.clearance.heading.involvementDatesTo')}
            usePvqFromDate
            usePvqToDate
            allowPresent={false}
          />
        </Field>
        <Field
          title={i18n.t('substance.pvq.drugs.clearance.heading.howManyTimes')}
          attribute={`${attribute}.HowManyTimes`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Text
            {...this.props.HowManyTimes}
            name="HowManyTimes"
            attribute={`${attribute}.HowManyTimes`}
            className="howManyTimes"
            onUpdate={this.updateHowManyTimes}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>
        <Field
          title={i18n.t('substance.pvq.drugs.clearance.heading.reason')}
          attribute={`${attribute}.Reason`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            {...this.props.Reason}
            name="Reason"
            attribute={`${attribute}.Reason`}
            className="reason"
            onUpdate={this.updateReason}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

      </div>
    ) : (
      <div className="drug-clearance-use">
        <Field
          attribute={`${attribute}.Description`}
          title={i18n.t('substance.drugs.clearance.heading.description')}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            attribute={`${attribute}.Description`}
            name="Description"
            className="description"
            {...this.props.Description}
            onUpdate={this.updateDescription}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          attribute={`${attribute}.InvolvementDates`}
          title={i18n.t('substance.drugs.clearance.heading.involvementDates')}
          adjustFor="daterange"
          help="substance.drugs.clearance.help.involvementDates"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateRange
            attribute={`${attribute}.InvolvementDates`}
            name="InvolvementDates"
            className="involvement-dates"
            {...this.props.InvolvementDates}
            minDateEqualTo
            onUpdate={this.updateInvolvementDates}
            onError={this.props.onError}
            required={this.props.required}
            hideFromDay
            hideToDay
          />
        </Field>

        <Field
          attribute={`${attribute}.EstimatedUse`}
          title={i18n.t('substance.drugs.clearance.heading.estimatedUse')}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Text
            attribute={`${attribute}.EstimatedUse`}
            name="EstimatedUse"
            className="estimated-use"
            {...this.props.EstimatedUse}
            onUpdate={this.updateEstimatedUse}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>
        <Comments
          title={i18n.t('substance.comments.optional')}
          name="Comments"
          className="optional-comment"
          attribute="Comments"
          {...this.props.Comments}
          onUpdate={this.updateComments}
          required={false}
        />
      </div>
    );
  }
}

DrugClearanceUse.defaultProps = {
  onError: (value, arr: ErrorCode[]) => arr,
  Comments: {},
  DrugType: {}, // PVQ Only
  DrugTypeOther: {}, // PVQOnly
  HowManyTimes: {}, // PVQOnly
  Reason: {}, // PVQOnly
};
